import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Home, Truck, MapPin, Info, MessageSquare } from 'lucide-react';
import { Button } from './ui/Button';
interface SiteHeaderProps {
  currentPage?: string;
  onNavigate?: (page: string) => void;
}
export function SiteHeader({
  currentPage = 'home',
  onNavigate
}: SiteHeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  // Track scroll position - check if past hero section
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    // Check initial scroll position
    handleScroll();
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navItems = [{
    id: 'home',
    label: 'Home',
    icon: Home
  }, {
    id: 'shipping',
    label: 'Ship Now',
    icon: Truck
  }, {
    id: 'tracking',
    label: 'Track',
    icon: MapPin
  }, {
    id: 'about',
    label: 'About',
    icon: Info
  }, {
    id: 'contact',
    label: 'Contact',
    icon: MessageSquare
  }];
  const handleNavigate = (page: string) => {
    onNavigate?.(page);
    setMobileMenuOpen(false);
  };
  // Pages with hero sections that need transparent header
  const pagesWithHero = ['home', 'about'];
  const hasHeroSection = pagesWithHero.includes(currentPage);
  const showTransparent = hasHeroSection && !isScrolled && !mobileMenuOpen;
  return <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${showTransparent ? 'bg-transparent border-transparent' : 'bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm'}`} data-id="element-1206">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1207">
          <div className="flex items-center justify-between py-2 sm:py-3" data-id="element-1208">
            {/* Logo */}
            <button onClick={() => handleNavigate('home')} className="flex items-center justify-center group" data-id="element-1209">
              <img src="https://cdn.magicpatterns.com/uploads/vbPywZMiUoiHS4U2ti4APK/Task_Runner_Logo-removebg-preview.png" alt="TaskRunner" className={`h-16 sm:h-20 w-auto object-contain transition-all ${showTransparent ? 'brightness-0 invert' : ''}`} data-id="element-1210" />
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1" data-id="element-1211">
              {navItems.map(item => {
              const isActive = currentPage === item.id;
              return <button key={item.id} onClick={() => handleNavigate(item.id)} className={`relative px-4 py-2 text-sm font-medium rounded-full transition-all ${isActive ? showTransparent ? 'text-white bg-white/20 backdrop-blur-sm' : 'text-brand bg-brand-50' : showTransparent ? 'text-white/70 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`} data-id="element-1212">
                    {item.label}
                  </button>;
            })}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-4" data-id="element-1213">
              <Button variant={showTransparent ? 'white' : 'primary'} size="sm" onClick={() => handleNavigate('shipping')} data-id="element-1214">
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className={`md:hidden p-2.5 -mr-2 rounded-lg transition-colors ${showTransparent ? 'text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 active:bg-gray-100'}`} aria-label="Toggle menu" data-id="element-1215">
              {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-1216" /> : <Menu className="w-6 h-6" data-id="element-1217" />}
            </button>
          </div>
        </div>
      </header>

      {/* Spacer to prevent content from going under fixed header (only for pages without hero) */}
      {!hasHeroSection && <div className="h-20 sm:h-[104px]" data-id="element-1218" />}

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-1219">
        {mobileMenuOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setMobileMenuOpen(false)} className="md:hidden fixed inset-0 bg-black/20 z-40" data-id="element-1220" />

            {/* Menu Panel */}
            <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="md:hidden fixed top-20 sm:top-[104px] left-0 right-0 bg-white border-b border-gray-200 shadow-lg z-50" data-id="element-1221">
              <nav className="max-w-7xl mx-auto px-4 py-4 space-y-1" data-id="element-1222">
                {navItems.map((item, index) => <motion.button key={item.id} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.05
            }} onClick={() => handleNavigate(item.id)} className={`w-full flex items-center gap-3 px-4 py-3.5 text-left font-medium rounded-xl transition-colors ${currentPage === item.id ? 'bg-brand-50 text-brand' : 'text-gray-700 active:bg-gray-100'}`} data-id="element-1223">
                    <item.icon className="w-5 h-5" data-id="element-1224" />
                    {item.label}
                  </motion.button>)}

                <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: navItems.length * 0.05
            }} className="pt-3 mt-3 border-t border-gray-100" data-id="element-1225">
                  <Button fullWidth size="lg" onClick={() => handleNavigate('shipping')} className="h-12" data-id="element-1226">
                    Get Started
                  </Button>
                </motion.div>
              </nav>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}